import { Injectable } from '@angular/core';
import { isEmpty, isNil } from 'lodash';
import { environment } from '@env/environment';
import * as moment from 'moment';
import { AuthService } from './auth.service';
import { FilterOptionTypes, MeterReadingType, MsbCategories } from '../enums';
import { FilterOptions } from '@app/shared/models/meter-data/meter-operator-filter';

@Injectable({
  providedIn: 'root',
})
export class MeterFilterService {
  private globalize = require('globalize');
  uknownType = 'Unknown';
  alphabets: string[] = [];
  private allowedMeterReadingKomKeys: any[] = [];
  private allowedEnergyDataKomKeys: any[] = [];
  private allMeterOperators: any[] = [];
  private noKomKeyMunicipality: any[] = [];

  constructor(public authService: AuthService) {
    this.allowedMeterReadingKomKeys = environment.allowedMeterReadingKomKeys.split(';');
    this.allowedEnergyDataKomKeys = environment.allowedEnergyDataKomKeys.split(';');
    this.noKomKeyMunicipality = environment.noKomKeyMunicipality.split(';');
  }

  setMeasurementLocationKey(measurementLocationKey: string): string {
    return `measurementLocationKey eq '${measurementLocationKey}' `;
  }

  setMunicipalityFilter(municipalityKey: string): string {
    return `municipalityKey eq '${municipalityKey}' `;
  }

  setSearchTextFilter(searchText: string, isDistinct: boolean): string {
    let q = '';
    if (searchText.length > 0) {
      q += !isDistinct ? `(contains(notes, '${searchText.trim()}')` : `(contains(meterNotes, '${searchText.trim()}')`;
      q += ` or contains(meterTypesName, '${searchText.trim()}')`;
      q += ` or contains(buildingTypesName, '${searchText.trim()}')`;
      q += ` or contains(meterNumber, '${searchText.trim()}')`;
      q += ` or contains(marketLocationKey, '${searchText.trim()}')`;
      q += ` or contains(street, '${searchText.trim()}')`;
      q += ` or contains(houseNumber, '${searchText.trim()}')`;
      q += ` or contains(zipCode, '${searchText.trim()}')`;
      q += ` or contains(district, '${searchText.trim()}'))`;

      const date = moment(this.globalize.parseDate(searchText));
      if (date.isValid()) {
        q += `or countDate eq ${date.year()}-${date.month() + 1}-${date.date()}) `;
      }
    }

    return q;
  }

  setKomKeyFilters(isMeterReadingTab: boolean, municipalityKey: string): string {
    let q = '';
    if (this.noKomKeyMunicipality.indexOf(municipalityKey) < 0) {
      const komKeys: string[] = isMeterReadingTab ? this.allowedMeterReadingKomKeys : this.allowedEnergyDataKomKeys;
      if (!isNil(komKeys) && !isEmpty(komKeys)) {
        const vals = komKeys.map((a) => `'${a}'`).join(',');
        q += `komKey in (${vals}) `;
      }
    }
    return q;
  }

  setSmartMeterFilter(isIMSysInstalled: boolean = false): string {
    return `isIMSysInstalled eq ${isIMSysInstalled} `;
  }

  setGasTempFilter(): string {
    let q = '';
    if (environment.environment === 'production') {
      q += `energyTypesName eq 'Strom' `;
    }

    return q;
  }

  setContractAccountFilter(contractAccount: string): string {
    let q = '';
    if (!isEmpty(contractAccount)) {
      q += `contractAccount eq '${contractAccount}' `;
    }

    return q;
  }

  setMarketLocationKeyFilter(marketLocationKey: string): string {
    let q = '';
    if (!isEmpty(marketLocationKey)) {
      q += `marketLocationKey eq '${marketLocationKey}' `;
    }

    return q;
  }

  setMeterTypesCodeFilter(meterTypesCodeFilter: string[]): string {
    let q = '';
    if (meterTypesCodeFilter !== null && meterTypesCodeFilter.length > 0) {
      const vals = meterTypesCodeFilter.map((a) => `'${a}'`);
      q += `meterTypeCode in (${vals}) `;
    } else {
      q += `meterTypeCode eq '${this.uknownType}' `;
    }

    return q;
  }

  setMeterNumbersFilter(meterNumbers: string[]): string {
    let q = '';
    if (!isNil(meterNumbers) && !isEmpty(meterNumbers)) {
      const vals = meterNumbers.map((a) => `'${a}'`).join(', ');
      q += `meterNumber in (${vals}) `;
    }
    return q;
  }

  setObisIdsFilter(obisIds: string[]): string {
    let q = '';
    if (!isNil(obisIds) && !isEmpty(obisIds)) {
      const vals = obisIds.map((a) => `'${a}'`);
      q += `ObisId in (${vals})`;
    }
    return q;
  }

  setMeterNumberFilter(meterNumber: string): string {
    let q = '';
    if (!isEmpty(meterNumber)) {
      q += `contains(meterNumber, '${meterNumber}') `;
    }
    return q;
  }

  setMeterIdsFilter(meterIds: string[]): string {
    let q = '';
    if (!isNil(meterIds) && !isEmpty(meterIds)) {
      q += `guid in (${meterIds.join(',')}) `;
    }
    return q;
  }

  setMeterTypesFilter(meterTypesFilter: string[], isForMeterReading: boolean): string {
    let q = '';
    if (isForMeterReading) {
      return q;
    }
    if (meterTypesFilter !== null && meterTypesFilter.length > 0) {
      const vals = meterTypesFilter.map((a) => `'${a}'`);
      q += `meterTypesName in (${vals}) `;
    } else {
      q += `meterTypesName eq '${this.uknownType}' `;
    }
    return q;
  }

  setEnergyTypesFilter(energyTypesFilter: string[], isForMeterReading: boolean): string {
    let q = '';
    if (isForMeterReading) {
      return q;
    }
    if (energyTypesFilter !== null && energyTypesFilter.length > 0) {
      const vals = energyTypesFilter.map((a) => `'${a}'`);
      q += `energyTypesName in (${vals}) `;
    } else {
      q += `energyTypesName eq '${this.uknownType}' `;
    }

    return q;
  }

  setEnergyGuardOnlyLicensedFilter(): string {
    return `isEnergyGuardLicenseAvailable eq true `;
  }

  setMarketOperatorFilter(marketOperatorFilters: string[], isForMeterReading: boolean, meterFilterOptions: FilterOptions[]): string {
    let q = '';
    if (isForMeterReading) {
      return q;
    }

    this.allMeterOperators = [];
    const selectedMarketOperators: any[] = marketOperatorFilters.reduce(
      (arr: string[], curr) => arr.concat(curr.split(';').filter((operator) => operator !== '')),
      []
    );
    if (selectedMarketOperators !== null && selectedMarketOperators.length > 0) {
      if (selectedMarketOperators.length === 1 && selectedMarketOperators[0] === '[null]') {
        q = `msbid eq null`;
      } else {
        let query1 = '';
        let query2 = '';
        let query3 = '';
        if (selectedMarketOperators.indexOf('others') > -1) {
          const operators = meterFilterOptions
            .filter((options) => options.type === FilterOptionTypes.MSB)
            .map((data) => {
              return {
                filterValue: data.msbCategory === MsbCategories.GMSB && data.msbid !== 'others' ? data.msbid.concat('[null]') : data.msbid,
              };
            });
          operators.forEach((filter) => {
            if (filter.filterValue !== 'others') {
              this.allMeterOperators = this.allMeterOperators.concat(filter.filterValue.split(';'));
            }
          });
          const isNullInSelected = selectedMarketOperators.indexOf('[null]');
          const nullIndex = this.allMeterOperators.indexOf('[null]');
          if (nullIndex > -1) {
            this.allMeterOperators.splice(nullIndex, 1);
            if (isNullInSelected < 0) {
              query1 += `msbId ne null `;
            }
          }

          if (this.allMeterOperators.length > 0) {
            const vals = selectedMarketOperators.map((msbId: string) => (msbId === '[null]' ? 'null' : `'${msbId}'`)).join(',');
            query2 += `msbId in (${vals}) eq false `;
          }
        }

        if (selectedMarketOperators.length > 0) {
          const vals = selectedMarketOperators.map((msbId: string) => (msbId === '[null]' ? 'null' : `'${msbId}'`)).join(',');
          query3 += `msbId in (${vals}) `;
        }

        let group1: string = '';
        if (query2.length > 0 && query3.length > 0) {
          group1 = `(${query2} or ${query3}) `;
        } else if (query2.length > 0 && query3.length <= 0) {
          group1 = query2;
        } else if (query2.length <= 0 && query3.length > 0) {
          group1 = query3;
        }

        if (query1.length > 0 && group1.length > 0) {
          q = `(${query1} and ${group1}) `;
        } else if (query1.length > 0 && group1.length <= 0) {
          q = query1;
        } else if (query1.length <= 0 && group1.length > 0) {
          q = group1;
        }
      }
    } else {
      q += `msbid eq '${this.uknownType}' `;
    }

    return q;
  }

  filterOutEmptyRegisterNumberForMeterReading(isForMeterReading: boolean): string {
    let q = '';
    if (isForMeterReading) {
      q += `registerNumber ne null `;
    }
    return q;
  }

  setActiveOnlyFilter(usageFilter: string | null = null): string {
    let q = '';
    q = `(usageEndDate eq null`;
    if (usageFilter === 'isMandatory') {
      const date = moment().subtract(2, 'months');
      q += ` or usageEndDate gt ${date.year()}-${date.month() + 1}-${date.date()}) `;
    } else if (usageFilter === 'isHistoric') {
      const date = moment().subtract(5, 'years');
      q += ` or usageEndDate gt ${date.year()}-${date.month() + 1}-${date.date()}) `;
    } else {
      q += ') ';
    }
    return q;
  }

  private setMeterMsbcontractsActiveOnlyFilter(usageFilter: string | null = null): string {
    let filterOption = `endDate eq null`;
    if (usageFilter === 'isMandatory') {
      const date = moment().subtract(2, 'months');
      filterOption += ` or endDate gt ${date.year()}-${date.month() + 1}-${date.date()}`;
    }

    return filterOption;
  }

  setBuildingFilter(connectionObjectNumber: string[]): string {
    let q = '';
    if (connectionObjectNumber.length > 0) {
      const vals = connectionObjectNumber.map((a) => `'${a}'`);
      q += `connectionObjectNumber in (${vals}) `;
    }
    return q;
  }

  setAbleseeinheitFilter(): string {
    return `((substring(readingGrouping,3,1) in ('B','D')) eq false or readingGrouping eq null)`;
  }

  setSensorFilter(): string {
    return `deviceEUI ne null `;
  }

  setReadingDateFilter(): string {
    let q = '';
    const date = moment().subtract(2, 'months');
    q += ` readingDate gt ${date.year()}-${date.month() + 1}-${date.date()} `;
    return q;
  }

  public getMeterReadingsAll(usageEndDateFilter: string | null, meterReadingType: MeterReadingType): string {
    let params = '';

    const activeOnlyFilter = this.setActiveOnlyFilter(usageEndDateFilter);
    if (activeOnlyFilter.length > 0) {
      params += ` and ${activeOnlyFilter}`;
    }

    const selectColumns = [
      'Guid',
      'MeterNumber',
      'MarketLocationKey',
      'Street',
      'HouseNumber',
      'HouseNumberAdditive',
      'ZipCode',
      'District',
      'ObjectType',
      'Notes',
      'ReadingGrouping',
      'IsImSysInstalled',
      'contractAccount',
      'deviceId',
      'usageEndDate',
      'komKey',
      'MeasurementLocationKey',
      'BillingCategory',
      'ConnectionObjectNumber',
    ].join(',');

    const selectOption = `$select=${selectColumns}`;

    const energyTypeExpand = 'EnergyType($select=Name)';
    const meterTypeExpand = 'MeterType($select=Code,Name)';

    let meterRegistersExpandOptions = ['$filter=ObisId ne null', '$select=ObisId,RegisterNumber,RegisterType', '$orderby=RegisterNumber'].join(';');
    let meterMsbcontractsExpand = '';

    if (meterReadingType === MeterReadingType.SPACE) {
      const cutoffDate = this.getUtcMonthsAgo(18);
      const cutoffLiteral = this.toODataEdmDate(cutoffDate);

      const meterReadingsExpand = ['$select=ReadingDate,Value', `$filter=ReadingDate ge ${cutoffLiteral}`, '$orderby=ReadingDate desc'].join(';');
      meterRegistersExpandOptions = [
        '$filter=ObisId ne null',
        '$select=ObisId,RegisterNumber,RegisterType',
        '$orderby=RegisterNumber',
        `$expand=MeterReadings(${meterReadingsExpand})`,
      ].join(';');

      const meterMsbcontractsFilter = this.setMeterMsbcontractsActiveOnlyFilter(usageEndDateFilter);
      const meterMsbcontractsExpandOptions = ['$select=VnbId', `$filter=${meterMsbcontractsFilter}`, '$orderby=VnbId desc'].join(';');
      meterMsbcontractsExpand = `MeterMsbcontracts(${meterMsbcontractsExpandOptions})`;
    }

    const meterRegistersExpand = `MeterRegisters(${meterRegistersExpandOptions})`;

    const buildingExpand = 'Building($select=guid;$expand=BuildingType($select=name))';
    const deviceExpand = 'Device($select=onboardingKey)';

    const expandColumns = [
      `${energyTypeExpand}`,
      `${meterTypeExpand}`,
      `${meterRegistersExpand}`,
      `${buildingExpand}`,
      `${deviceExpand}`,
      `${meterMsbcontractsExpand}`,
    ]
      .filter(Boolean)
      .join(',');

    const expandOption = `$expand=${expandColumns}`;

    params += `&${selectOption}`;
    params += `&${expandOption}`;
    params += '&$count=true';

    return params;
  }

  private getUtcMonthsAgo(monthsAgo: number): Date {
    const now = new Date();
    const d = new Date(now.getTime());
    d.setUTCMonth(d.getUTCMonth() - monthsAgo);
    d.setUTCHours(0, 0, 0, 0);
    return d;
  }

  private toODataEdmDate(date: Date): string {
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}
